import { Alert, AlertTitle, AlertDescription } from "components/ui/alert";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Skeleton } from "components/ui/skeleton";
import { Spinner } from "components/ui/spinner";
import { Textarea } from "components/ui/textarea";
import { Field, FieldError, FieldLabel } from "components/ui/field";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { TooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { ApiKey, ApiProduct, ModifyApiKey } from "data/models";
import { createApiKey } from "data/mutations-common";
import { fetchApiKeys, fetchApiProducts } from "data/queries-common";
import { Form, Formik } from "formik";
import { AlertCircle, Gem, Info } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generatePath, useHistory } from "react-router";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { createApiKeyValidationSchema } from "components/api-keys/validation";

interface CreateAppDialogProps {
  open: boolean;
  handleClose: () => void;
}

export const CreateAppDialog = (props: CreateAppDialogProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const apiKeysQuery = useQuery({
    queryKey: [QueryKeys.apiKeys, identityId],
    queryFn: () => fetchApiKeys(),
  });
  const apiProductsQuery = useQuery({
    queryKey: [QueryKeys.apiProducts],
    queryFn: () => fetchApiProducts(),
  });
  const history = useHistory();

  const queryClient = useQueryClient();
  const apiKeyCreateMutation = useMutation({
    mutationFn: (values: ModifyApiKey) => createApiKey(values),
    onSuccess: (createdApiKey) => {
      let allApiKeys = [createdApiKey];
      if (apiKeysQuery.isSuccess) {
        allApiKeys = [...apiKeysQuery.data, createdApiKey];
      }
      queryClient.setQueryData([QueryKeys.apiKeys, identityId], allApiKeys);
      props.handleClose();
      history.push(
        generatePath(Routes.developerApiKeysEditKey, {
          keyName: createdApiKey.name,
        }),
        {
          keyName: createdApiKey.name,
        },
      );
    },
  });

  const isLoading = apiKeysQuery.isPending || apiProductsQuery.isPending;
  const isSuccess = apiKeysQuery.isSuccess && apiProductsQuery.isSuccess;
  const isQueryError = apiKeysQuery.isError || apiProductsQuery.isError;
  const isMutationError = apiKeyCreateMutation.isError;
  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  const premiumPermissionKeys = [PermissionsKeys.premiumDataRead];
  const isDisabledPremiumData = (isPremium?: boolean) => {
    if (isPremium === undefined) {
      return false;
    }
    return isPremium && !isPermitted(premiumPermissionKeys);
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.handleClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {isQueryError && (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              An error occurred while fetching the API details.
            </AlertDescription>
          </Alert>
        )}

        {isSuccess && (
          <Formik<ModifyApiKey>
            enableReinitialize={true}
            initialValues={{
              name: "",
              status: "pending",
              products: [],
              notes: "",
              displayName: "",
            }}
            onSubmit={async (values) => {
              apiKeyCreateMutation.mutate(values);
            }}
            validationSchema={createApiKeyValidationSchema}
          >
            {({
              values: { name, products, notes },
              handleChange,
              handleBlur,
              isValid,
              dirty,
              resetForm,
              errors,
              touched,
              setFieldValue,
            }) => {
              return (
                <Form className="space-y-4">
                  <Field>
                    <FieldLabel htmlFor="name">Name</FieldLabel>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={apiKeyCreateMutation.isPending}
                      maxLength={60}
                      aria-invalid={touched.name && Boolean(errors.name)}
                    />
                    {touched.name && errors.name && (
                      <FieldError>{errors.name}</FieldError>
                    )}
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="notes">Note</FieldLabel>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={apiKeyCreateMutation.isPending}
                      maxLength={200}
                      aria-invalid={touched.notes && Boolean(errors.notes)}
                      className="field-sizing-fixed min-h-20"
                    />
                    {touched.notes && errors.notes && (
                      <FieldError>{errors.notes}</FieldError>
                    )}
                  </Field>

                  <div className="space-y-2">
                    <Label>API Products</Label>
                    <div className="grid grid-cols-1 gap-2 pl-2 md:grid-cols-2">
                      {apiProductsQuery.data
                        .sort((a: ApiProduct, b: ApiProduct) =>
                          a.displayName.toUpperCase() >
                          b.displayName.toUpperCase()
                            ? 1
                            : -1,
                        )
                        .map((product: ApiProduct) => {
                          const isFullPremium =
                            product.premium && product.premium === "full";
                          const isPartialPremium =
                            product.premium && product.premium === "partial";
                          const disabled =
                            apiKeyCreateMutation.isPending ||
                            isDisabledPremiumData(isFullPremium || undefined);

                          return (
                            <div
                              key={product.name}
                              className="flex items-center gap-2"
                            >
                              <Checkbox
                                id={`product-${product.name}`}
                                name="products"
                                value={product.name}
                                checked={products.includes(product.name)}
                                disabled={disabled}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setFieldValue("products", [
                                      ...products,
                                      product.name,
                                    ]);
                                  } else {
                                    setFieldValue(
                                      "products",
                                      products.filter(
                                        (p: string) => p !== product.name,
                                      ),
                                    );
                                  }
                                }}
                              />
                              <Label
                                htmlFor={`product-${product.name}`}
                                className="font-normal"
                              >
                                {product.displayName}
                              </Label>
                              {isFullPremium && (
                                <TooltipWrapper
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                  }}
                                  title={
                                    isDisabledPremiumData(true)
                                      ? permissionDeniedMessageString(
                                          premiumPermissionKeys,
                                        )
                                      : "Premium Data"
                                  }
                                >
                                  <Gem color="#1876d3" />
                                </TooltipWrapper>
                              )}
                              {isPartialPremium && (
                                <TooltipWrapper
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                  }}
                                  title={
                                    isDisabledPremiumData(false)
                                      ? permissionDeniedMessageString(
                                          premiumPermissionKeys,
                                          {
                                            upgrade_required:
                                              "Some functionality is limited to Team or higher",
                                          },
                                        )
                                      : "Premium Data"
                                  }
                                >
                                  <Info />
                                </TooltipWrapper>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Info className="size-4" />
                      <span>
                        Products have some functionality that is only accessible
                        when you have a Team Plan or higher
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Gem className="size-4" color="#1876d3" />
                      <span>
                        Products are only accessible when you have a Team Plan
                        or higher
                      </span>
                    </div>
                  </div>

                  {isMutationError && (
                    <Alert variant="destructive">
                      <AlertCircle />
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>
                        An error occurred while creating the API Key.
                      </AlertDescription>
                    </Alert>
                  )}

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={apiKeyCreateMutation.isPending}
                      onClick={() => {
                        resetForm();
                        apiKeyCreateMutation.reset();
                        props.handleClose();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        !isValid || !dirty || apiKeyCreateMutation.isPending
                      }
                    >
                      {apiKeyCreateMutation.isPending && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};
